/**
 * The two sentences at the top of the Overview screen.
 *
 * Out of `Overview.tsx` for the reason `summariseCaps` and the role ladder are:
 * `node --test` refuses a `.tsx`, so anything left in the component is beyond
 * the reach of anything that runs. Here it is the sharpest case of that rule,
 * because the layer that runs is the layer a person reads — these strings are
 * what an operator acts on before lending an ad account.
 *
 * `LoadState` is written out rather than imported from `useLoad.ts`. The type
 * would be the tidier import, and it would drag `api.ts` in behind it, which
 * reads `import.meta.env` through `config.ts` — and then this module would
 * typecheck, build, and fail to load under the runner. That is the same shape
 * three times over now, so it is spelled out instead.
 *
 * Nothing here decides anything. It states what was counted, and its one duty is
 * to never state more than was counted.
 */
import { summariseCaps } from "./format.ts";

export type LoadState = "loading" | "ready" | "failed";

/** One banner, ready to render. The screen decides nothing about it. */
export interface OverviewBanner {
	kind: "warning" | "danger";
	title: string;
	body: string;
}

/**
 * Everything the Overview screen says about caps. No counts and no flags leave
 * this module — only finished sentences and a list of banners to render in the
 * order given. A screen that receives one number cannot add it to another.
 */
export interface OverviewCopy {
	headline: string;
	subhead: string;
	banners: OverviewBanner[];
}

/**
 * One call, and the screen renders what comes back.
 *
 * This exists to remove a site rather than to guard one. Three guards were
 * added to this file's neighbourhood in a day — `summariseCaps` under test, the
 * screen pinned as delegating, these sentences pinned as functions — and each
 * time the defect moved one step and passed. The last hiding place was between
 * delegation and phrasing:
 *
 *   const c = summariseCaps(list);
 *   const uncapped = c.uncapped + c.unreadable;   // then phrased perfectly
 *
 * Every guard was satisfied and the operator was told that accounts we could
 * not read have no spending limit — in the subhead, in the banner, and by the
 * absence of the unreadable banner, three times at once.
 *
 * The lie is born where the numbers are added together. So the screen no longer
 * holds two numbers it could add: it holds one result and renders it.
 *
 * Banners are built here too, and that is not tidiness. Returning `warnUncapped`
 * and `unreadable` moved the same fold into the JSX — `(warnUncapped ||
 * unreadable > 0)` with the danger banner deleted passed 57/0, measured. A list
 * of finished banners leaves the screen no condition to combine.
 */
export function overviewCopy(
	state: LoadState,
	accounts: Array<{
		spendCap: string | null;
		amountSpent: string | null;
		currency: string | null;
	}>,
	truncated: boolean,
): OverviewCopy {
	const { uncapped, unreadable, nearLimit, spendUnreadable } = summariseCaps(accounts);
	const banners: OverviewBanner[] = [];
	if (truncated)
		banners.push({
			kind: "warning",
			title: "These numbers are floors, not totals.",
			body: "More accounts exist than one read returns, so the counts above and the cards below cover the first 1000 only. Nothing is missing from the database.",
		});
	// Unreadable before uncapped, for the reason the subhead states it first:
	// "no limit" is actionable on this screen, "cannot tell" sends the operator
	// to Business Manager. The more urgent one should not be the lower one.
	if (unreadable > 0)
		banners.push({
			kind: "danger",
			title:
				unreadable === 1
					? "One account's spending limit could not be read."
					: `${unreadable} accounts' spending limits could not be read.`,
			body: "The mirror holds a cap value that does not read as an amount, so this console cannot tell you whether a limit exists — look for “Limit unreadable” on the cards below. Check those accounts in Business Manager before lending them, and re-sync on the Ad accounts page.",
		});
	/*
	 * Near the limit comes above "no limit at all", and the order is the
	 * argument. An account at 97% of its cap will stop delivering today; an
	 * uncapped account is a standing risk that has been standing for a while.
	 * Urgency, not severity, decides which one a person reads first.
	 */
	if (nearLimit > 0)
		banners.push({
			kind: "danger",
			title:
				nearLimit === 1
					? "One account is close to its spending limit."
					: `${nearLimit} accounts are close to their spending limits.`,
			body: "At least 90% of the limit is already spent, so delivery stops when it is reached — this console has no other hard stop. Raise the limit in Business Manager, or let it stop on purpose rather than by surprise.",
		});
	if (uncapped > 0)
		banners.push({
			kind: "warning",
			title: "An account has no spending limit.",
			body: "This console has no platform-side spend guard by design — the Meta account limit is the only hard stop. Set one in Business Manager.",
		});
	/*
	 * Last, and quieter than the rest, because it is a gap in what we know rather
	 * than a state of an account. The cap is readable on every one of these — so
	 * the hard stop exists and is shown — and what is missing is how close to it
	 * they are.
	 */
	if (spendUnreadable > 0)
		banners.push({
			kind: "warning",
			title:
				spendUnreadable === 1
					? "One account's spend could not be read."
					: `${spendUnreadable} accounts' spend could not be read.`,
			body: "The limit on these is known and shown, but the amount spent against it is not, so the rail is left unfilled rather than drawn at zero. Re-sync on the Ad accounts page; if it persists, read the figure in Business Manager before assuming there is room.",
		});
	return {
		headline: headline(state, accounts.length, truncated),
		subhead: subhead(state, accounts.length, uncapped, unreadable, truncated),
		banners,
	};
}

function headline(state: LoadState, count: number, truncated: boolean): string {
	if (state === "loading") return "Loading your accounts…";
	if (state === "failed") return "The console database is unreachable.";
	// `count` is the caller's filtered share; `truncated` is the read behind it.
	// Both can be true at once — a tenant whose grants sit past the ceiling sees
	// none of their own — and "At least 0" is not a sentence.
	if (truncated)
		return count === 0
			? "No ad accounts among the ones we could read."
			: `At least ${count} ad accounts under your control.`;
	if (count === 0) return "No ad accounts yet.";
	return count === 1
		? "One ad account under your control."
		: `${count} ad accounts under your control.`;
}

function subhead(
	state: LoadState,
	count: number,
	uncapped: number,
	unreadable: number,
	truncated: boolean,
): string {
	if (state === "loading") return "Reading the accounts you have been granted.";
	if (state === "failed")
		return "Every screen reads CloudSQL, so nothing lists until the database answers again.";
	/*
	 * Truncated first, because it changes what every other number means: each
	 * becomes a floor over the rows that were read, not a total.
	 *
	 * Both states are reported here rather than only `uncapped`. The merge that
	 * brought truncation together with the unreadable state produced a version
	 * mentioning neither the unreadable count nor its floor — silently dropping,
	 * under truncation, the very distinction the unreadable state exists to make.
	 * "At least N have no limit" would have been said while accounts whose limit
	 * could not be read went unmentioned.
	 */
	if (truncated) {
		// The caller's share can be empty while the read was still cut off — a
		// tenant whose grants sit past the ceiling sees none of their own. Saying
		// "at least 0" there would be a sentence about nothing.
		if (count === 0)
			return "The read stopped at 1000 rows before reaching any of yours, which is not the same as having none.";
		const parts: string[] = [];
		if (uncapped > 0) parts.push(`at least ${uncapped} with no spending limit`);
		if (unreadable > 0) parts.push(`at least ${unreadable} whose limit could not be read`);
		return parts.length > 0
			? `Of the accounts read so far: ${parts.join(", ")}. Not all of them were read.`
			: "Every account read so far has a spending limit — but not all of them were read.";
	}
	if (count === 0)
		return "Sync the ad accounts from Meta on the Ad accounts page, then grant them out on Access.";
	if (unreadable > 0)
		return `${unreadable === 1 ? "One account's limit" : `${unreadable} accounts' limits`} could not be read.`;
	if (uncapped > 0)
		return `${uncapped === 1 ? "One account has" : `${uncapped} accounts have`} no spending limit.`;
	return "Every account has a spending limit set. Campaign changes still go through Business Manager.";
}
